<script>
import API from '@/api/Lead'
import Bus from '@/bus'

import { ModalComponent } from 'vue-elder-modal'
import Card from '@/components/Card-v2'
import ContactCard from '@/components/Contact/Card'
import Activities from './Activities.vue'
import Comments from './Comments.vue'
import Tasks from '@/components/Task/List/Card.vue'

export default {
  data() {
    return {
      lead: null,
      promise: null,
      listeners: null,
      item: null,
      intent: null,
    }
  },
  methods: {
    fetch() {
      this.promise = API.get({ id: this.lead }, { query: 'LeadModal' }).then(lead => {
        this.item = lead
        this.promise = null

        this.$nextTick(() => {
          switch (this.intent) {
            case 'call':
              // this.$el.querySelector('[href^="tel:"]').click()
              break
          }
        })
      })
    },
    reset() {
      this.lead = null
      this.item = null
      this.intent = null
      this.listeners = null
      this.promise = null
    },
    triggerListener(event, ...args) {
      if (!this.listeners[event]) return
      this.listeners[event](...args)
    },
  },
  computed: {
    actions() {
      if (!this.item) return

      const base = { iconPlacement: 'left', tag: 'a', theme: 'success' }

      switch (this.intent) {
        case 'call':
        case 'activity':
        case 'tasks':
          return [
            this.item.contact.phone && {
              ...base,
              icon: 'fa-pro-light:phone',
              label: this.item.contact.phone,
              href: `tel:${this.item.contact.phone}`,
            },
            {
              ...base,
              iconPlacement: 'left',
              icon: 'fa-pro-light:envelope',
              label: this.$t('sendEmail'),
              listeners: { click: () => (this.intent = 'message') },
              theme: 'tertiary',
            },
          ].filter(Boolean)
        case 'message':
          return [
            this.item.contact.phone && {
              ...base,
              icon: 'fa-pro-light:phone',
              label: this.item.contact.phone,
              href: `tel:${this.item.contact.phone}`,
            },
            this.item.contact.email && {
              ...base,
              iconPlacement: 'left',
              icon: 'fa-pro-light:envelope',
              label: this.item.contact.email,
              theme: 'tertiary',
              style: 'pointer-events: none;',
            },
          ].filter(Boolean)
      }
    },
  },
  created() {
    Bus.on('lead-modal:show', ({ lead, intent, listeners }) => {
      this.lead = lead
      this.intent = intent || this.intent
      this.listeners = listeners
      this.fetch()
    })
    Bus.on('lead-modal:hide', () => this.reset())
  },
  components: {
    ModalComponent,
    ContactCard,
    Activities,
    Card,
    Comments,
    Tasks,
  },
}
</script>

<template>
  <ModalComponent :show="Boolean(lead)" @close="reset" :clickaway="true">
    <Loader :value="promise" theme="default">
      <Card v-if="item" class="lead-modal">
        <template #header>
          <template v-if="$path('contact.company.name', item)">
            <div data-part="company">{{ item.contact.company.name }}</div>
            <div data-part="organizationNumber">{{ $t('orgNo') }} {{ item.contact.company.organizationNumber }}</div>
          </template>
          <div data-part="name">{{ item.contact.name }}</div>
          <div data-part="actions">
            <ButtonComponent v-for="{ listeners = {}, ...props } in actions" v-bind="props" v-on="listeners" />
          </div>
        </template>
        <template #default>
          <Activities
            v-if="['call', 'activity'].includes(intent)"
            :lead="lead"
            :scope="item.reference"
            anchoring="top"
            autofocus
            @create="a => triggerListener('create:activity', a)"
          />
          <Comments
            v-if="['message'].includes(intent)"
            :lead="lead"
            @processed="(c, all) => triggerListener('processed:comment', c, all)"
          />
          <Tasks
            v-if="['tasks'].includes(intent)"
            :reference="{ onModel: 'Lead', ref: lead }"
            @status="s => triggerListener('status:task', s)"
          />
        </template>
      </Card>
    </Loader>
  </ModalComponent>
</template>

<style lang="scss">
.lead-modal {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  z-index: get-layer('modal');

  .lead-activities {
    border: none;
    border-radius: 0;
    padding: 0;

    grid-template-columns: 1fr;
    grid-template-areas:
      'header'
      'input'
      'list';

    gap: 1.5rem;

    &__list {
      max-height: 350px;
    }

    &__table {
      display: flex;
      flex-direction: column-reverse;
    }

    & > .card-v2__content {
      display: contents;

      & > .elder-loader {
        display: contents;

        & > .lead-activities__table {
          display: contents;
        }
      }

      .lead-activities__list {
        grid-area: list;
      }

      .lead-activities__new-input {
        grid-area: input;
      }
    }

    & > .card-v2__header {
      grid-area: header;
    }
  }

  .comments__list {
    max-height: 480px;
  }

  & > .card-v2__header {
    border-bottom: 1px solid var(--k-ui-color-neutral);
    margin-bottom: 1rem;

    text-align: center;

    display: grid;
    justify-content: center;

    gap: 1rem;

    background-color: var(--k-ui-color-neutral-lightest);

    grid-template-areas:
      'name'
      'actions';

    &:has([data-part='company']) {
      grid-template-areas:
        'company company'
        'organizationNumber name'
        'actions actions';

      [data-part='name'] {
        font-size: 1rem;
        text-align: left;
      }

      [data-part='organizationNumber'] {
        text-align: right;
      }
    }

    [data-part] {
      line-height: 1;
    }

    [data-part='actions'] {
      grid-area: actions;
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 0.5rem;

      [type='button'] {
        border-radius: 999px;
      }
    }

    [data-part='name'] {
      grid-area: name;
      font-size: 2rem;
      margin: 0;
    }

    [data-part='company'] {
      grid-area: company;
      font-size: 2rem;
      font-weight: bold;
    }

    [data-part='organizationNumber'] {
      grid-area: organizationNumber;
    }
  }

  .card-v2 > .card-v2__header {
    padding: 0;
  }
  .card-v2 > .card-v2__content {
    padding: 0;
  }

  .comments {
    box-shadow: none;
    border: none;

    .card-v2__content,
    .card-v2__actions {
      padding: 0;
    }
  }

  .tasks-list-card {
    border: none;

    .card-v2__header {
      padding-bottom: 1rem !important;
    }

    .card-v2__title {
      visibility: hidden;
    }
  }
}

.elder-loader:has(.lead-modal) {
  display: contents;
}
</style>
